/* bb-cart-glow.js — Bara Bros cart, Chrome Fever / Liquid Glow.

   Presentation only. Every number this file shows comes from the section's
   [data-bb-glow-state] JSON, which Liquid renders from the real Shopify cart
   (Shopify-formatted, in the shopper's currency) and which is replaced on
   every section re-render. This file never adds, removes or changes a line.

   - Mobile checkout dock + order-summary sheet (one modal, one active CTA)
   - Pending state: checkout reads "Updating cart…" while a change is unresolved
   - Settled cart: after every burst of changes (and when the drawer opens) one
     /cart.js snapshot is read. Every count and checkout label on the page and
     in the drawer is set from it, and a section whose rendered state disagrees
     with it is re-rendered. Responses can arrive in any order; what the shopper
     ends up seeing is always what Shopify has.
   - Free-shipping progress from the Mobile bar block's country map, shown
     only when the shopper's currency is the store currency
   - Event-triggered feedback (confirmed add, confirmed quantity change,
     newly applied discount, threshold reached), each once per transition
*/
(function () {
  'use strict';

  if (window.__bbCartGlowInit) return;
  window.__bbCartGlowInit = true;

  var SHEET_OPEN_MS = 260;
  var SHEET_CLOSE_MS = 200;
  var REDUCED_MS = 100;
  var SLACK_MS = 120;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function pageRoot() {
    return document.querySelector('cart-items-component.bb-glow');
  }

  function drawerRoot() {
    return document.querySelector('cart-drawer.bb-glow');
  }

  function rewardsOn(root) {
    return !!root && root.getAttribute('data-bb-glow-rewards') !== 'false' && !reducedMotion();
  }

  function readState(root) {
    if (!root) return null;
    var el = root.querySelector('[data-bb-glow-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function plural(count) {
    return count === 1 ? 'item' : 'items';
  }

  // Adds a class for one run of its animation, then removes it.
  function flash(el, className, ms) {
    if (!el) return;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
    setTimeout(function () {
      el.classList.remove(className);
    }, ms + 60);
  }

  // ── Polite, brief announcements (never the whole cart) ────────────────
  var liveRegion = null;
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () {
      liveRegion.textContent = message;
    }, 60);
  }

  // ── Checkout label: "Checkout (N items)" in units ─────────────────────
  var checkoutWord = null;
  function rememberCheckoutWord() {
    if (checkoutWord) return;
    var label = document.querySelector('[data-bb-glow-cta-label]');
    if (!label) return;
    var text = (label.firstChild && label.firstChild.nodeType === 3 ? label.firstChild.nodeValue : label.textContent) || '';
    checkoutWord = text.split('(')[0].trim() || 'Checkout';
  }

  function ctaLabelHTML(count) {
    return (
      esc(checkoutWord || 'Checkout') +
      ' (' +
      count +
      '<span class="visually-hidden"> ' +
      plural(count) +
      '</span>)'
    );
  }

  // ── Pending cart mutations ─────────────────────────────────────────────
  var pending = false;
  var UPDATING = 'Updating cart…';

  function setPending(on) {
    pending = on;
    lockExpress(on);
    if (on) rememberCheckoutWord();
    document.querySelectorAll('[data-bb-glow-checkout]').forEach(function (btn) {
      var label = btn.querySelector('[data-bb-glow-cta-label]');
      if (on) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-bb-glow-pending', 'true');
        if (label && btn.getAttribute('aria-busy') !== 'true') label.textContent = UPDATING;
      } else if (btn.getAttribute('data-bb-glow-pending') === 'true') {
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('data-bb-glow-pending');
      }
    });
    [pageRoot(), document.getElementById('bb-glow-sheet')].forEach(function (el) {
      if (!el) return;
      if (on) el.setAttribute('data-bb-glow-pending', 'true');
      else el.removeAttribute('data-bb-glow-pending');
    });
    var dockTotal = document.querySelector('[data-bb-glow-dock-total]');
    if (dockTotal) dockTotal.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  // Checkout press: compress, then an honest pending label. No success
  // state is shown; the browser is simply navigating to checkout.
  var leaving = false;
  var leavingAt = 0;
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest('[data-bb-glow-checkout]');
      if (!btn) return;
      if (pending || leaving || btn.disabled || btn.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        if (pending) announce('Your cart is still updating. Try checkout again in a moment.');
        return;
      }
      leaving = true;
      leavingAt = Date.now();
      btn.classList.add('is-pressed');
      setTimeout(function () {
        btn.classList.remove('is-pressed');
      }, 100);
      var label = btn.querySelector('[data-bb-glow-cta-label]');
      if (label) {
        btn.setAttribute('data-bb-glow-label', label.innerHTML);
        label.textContent = 'Opening checkout…';
      }
      btn.setAttribute('aria-busy', 'true');
    },
    true
  );

  function restoreCheckoutButtons() {
    leaving = false;
    document.querySelectorAll('[data-bb-glow-checkout][aria-busy="true"]').forEach(function (btn) {
      var label = btn.querySelector('[data-bb-glow-cta-label]');
      var saved = btn.getAttribute('data-bb-glow-label');
      if (label && saved) label.innerHTML = saved;
      btn.removeAttribute('aria-busy');
    });
  }

  // Still on the page a moment after the press — the navigation failed or
  // was cancelled: the button works again (a second tap retries at once).
  ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (leaving && event.isTrusted && Date.now() - leavingAt > 2000) restoreCheckoutButtons();
      },
      { capture: true, passive: true }
    );
  });

  // Returning with Back (bfcache): a usable CTA, and the cart may have
  // changed in another tab, so re-read it rather than trusting a saved label.
  window.addEventListener('pageshow', function (event) {
    restoreCheckoutButtons();
    if (event.persisted) scheduleSettle('pageshow', 0);
  });

  // ── Dock + sheet (cart page) ───────────────────────────────────────────
  var sheet = null;
  var sheetState = 'closed';
  var sheetToken = 0;
  var sheetTrigger = null;
  var sheetInerted = [];
  var sheetScrollY = 0;

  function dock() {
    return document.querySelector('[data-bb-glow-dock]');
  }

  function renderDock(state) {
    var d = dock();
    if (!d) return;
    if (!state || !state.count) {
      d.hidden = true;
      if (sheetState !== 'closed') closeSheet();
      return;
    }
    d.hidden = false;
    var total = d.querySelector('[data-bb-glow-dock-total]');
    if (total) total.textContent = state.total;
    var note = d.querySelector('[data-bb-glow-dock-note]');
    if (note) {
      if (state.discountCents > 0) note.innerHTML = 'You’re saving <strong>' + esc(state.discount) + '</strong>';
      else if (state.compareSavingsCents > 0) note.innerHTML = 'Sale prices: <strong>' + esc(state.compareSavings) + '</strong> off';
      else note.textContent = 'Order summary';
    }
  }

  function renderSheet(state) {
    if (!sheet || !state) return;
    var totals = sheet.querySelector('[data-bb-glow-sheet-totals]');
    if (totals) {
      var rows =
        '<div class="bb-glow-totals__row"><dt>Items subtotal (' +
        state.count +
        ' ' +
        plural(state.count) +
        ')</dt><dd>' +
        esc(state.subtotal) +
        '</dd></div>';
      if (state.discountCents > 0) {
        rows +=
          '<div class="bb-glow-totals__row bb-glow-totals__row--savings"><dt>Offer savings' +
          (state.offers && state.offers.length
            ? '<span class="bb-glow-totals__titles">' + esc(state.offers.join(', ')) + '</span>'
            : '') +
          '</dt><dd>-' +
          esc(state.discount) +
          '</dd></div>';
      }
      rows +=
        '<div class="bb-glow-totals__row bb-glow-totals__row--total"><dt>Estimated total</dt><dd>' +
        esc(state.totalWithCurrency || state.total) +
        '</dd></div>';
      totals.innerHTML = rows;
    }
  }

  // Every checkout label (drawer, summary, dock, sheet) from one count.
  function renderLabels(count) {
    if (typeof count !== 'number') return;
    rememberCheckoutWord();
    document.querySelectorAll('[data-bb-glow-checkout] [data-bb-glow-cta-label]').forEach(function (label) {
      // A checkout press in progress keeps "Opening checkout…".
      if (label.closest('[data-bb-glow-checkout][aria-busy="true"]')) return;
      label.innerHTML = ctaLabelHTML(count);
    });
  }

  function renderCounts(count) {
    if (window.BBCart && typeof window.BBCart.renderCounts === 'function') {
      window.BBCart.renderCounts(count);
      return;
    }
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      var bubble = el.closest('.cart-count-bubble');
      el.textContent = count >= 100 && bubble && window.cartStrings ? window.cartStrings.item_99 : count;
    });
  }

  function measureDock() {
    var d = dock();
    var root = pageRoot();
    if (!d || !root || typeof ResizeObserver === 'undefined') return;
    var ro = new ResizeObserver(function () {
      var h = d.hidden ? 0 : Math.ceil(d.getBoundingClientRect().height);
      root.style.setProperty('--bbg-dock-h', h + 'px');
      // The page's own end (footer) also has to scroll clear of the fixed dock.
      document.body.style.setProperty('--bbg-dock-h', h + 'px');
    });
    ro.observe(d);
  }

  function sheetPanel() {
    return sheet ? sheet.querySelector('[role="dialog"]') : null;
  }

  function setSheetState(state) {
    sheetState = state;
    if (sheet) sheet.setAttribute('data-state', state);
  }

  function afterSheetTransition(token, ms, done) {
    var panel = sheetPanel();
    var finished = false;
    var timer = null;
    function finish() {
      if (finished) return;
      finished = true;
      if (panel) panel.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      if (token === sheetToken) done();
    }
    function onEnd(event) {
      if (event.target === panel) finish();
    }
    if (panel) panel.addEventListener('transitionend', onEnd);
    timer = setTimeout(finish, ms + SLACK_MS);
  }

  var sheetLocked = false;

  function lockForSheet() {
    if (sheetLocked) return;
    sheetLocked = true;
    sheetScrollY = window.scrollY;
    if (window.BBOverlay) {
      window.BBOverlay.lock('cart:summary-sheet', sheet);
    } else {
      document.body.classList.add('overflow-hidden');
      document.documentElement.setAttribute('scroll-lock', '');
    }
    // The page behind (and the dock, whose CTA would compete) goes inert.
    var targets = Array.prototype.slice.call(
      document.querySelectorAll(
        'body > :is(#header-group, main, footer, .skip-to-content-link, .shopify-section-group-footer-group, .shopify-section-group-header-group)'
      )
    );
    var d = dock();
    if (d) targets.push(d);
    sheetInerted = targets.filter(function (el) {
      return !el.inert && !el.contains(sheet);
    });
    sheetInerted.forEach(function (el) {
      el.inert = true;
    });
  }

  function unlockForSheet() {
    if (!sheetLocked) return;
    sheetLocked = false;
    sheetInerted.forEach(function (el) {
      el.inert = false;
    });
    sheetInerted = [];
    if (window.BBOverlay) {
      window.BBOverlay.unlock('cart:summary-sheet');
      return;
    }
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    if (Math.abs(window.scrollY - sheetScrollY) > 1) window.scrollTo(0, sheetScrollY);
  }

  function setToggleExpanded(open) {
    document.querySelectorAll('[data-bb-glow-sheet-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function openSheet(trigger) {
    if (!sheet) return;
    if (sheetState === 'open' || sheetState === 'opening') return;
    var fromClosed = sheetState === 'closed';
    var token = ++sheetToken;
    sheetTrigger = trigger || sheetTrigger;
    renderSheet(readState(pageRoot()));
    if (fromClosed) lockForSheet();
    setToggleExpanded(true);
    // Opening starts now; the attribute (and so the slide) follows next frame.
    sheetState = 'opening';

    function start() {
      if (token !== sheetToken) return;
      setSheetState('opening');
      var title = sheet.querySelector('#bb-glow-sheet-title');
      if (title) title.focus({ preventScroll: true });
      afterSheetTransition(token, reducedMotion() ? REDUCED_MS : SHEET_OPEN_MS, function () {
        setSheetState('open');
      });
    }
    if (fromClosed) {
      void sheet.offsetWidth;
      requestAnimationFrame(start);
    } else {
      start();
    }
  }

  function closeSheet() {
    if (!sheet || sheetState === 'closed' || sheetState === 'closing') return;
    var token = ++sheetToken;
    setSheetState('closing');
    setToggleExpanded(false);
    afterSheetTransition(token, reducedMotion() ? REDUCED_MS : SHEET_CLOSE_MS, function () {
      setSheetState('closed');
      unlockForSheet();
      var target =
        sheetTrigger && sheetTrigger.isConnected ? sheetTrigger : document.querySelector('[data-bb-glow-sheet-open]');
      if (target) target.focus({ preventScroll: true });
    });
  }

  function onSheetKeydown(event) {
    if (sheetState === 'closed') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSheet();
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = sheetPanel();
    if (!panel) return;
    var focusables = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initSheet() {
    var root = pageRoot();
    var found = root ? root.querySelector('#bb-glow-sheet') : null;
    if (!found) return;
    // A theme-editor reload renders a fresh sheet; drop the one moved earlier.
    if (sheet && sheet !== found) {
      if (sheetState !== 'closed') unlockForSheet();
      sheet.remove();
      sheetState = 'closed';
    }
    found.dataset.bbGlowReady = 'true';
    // Lift the sheet out of the cart section's stacking context so its scrim
    // covers the header too. It is outside the section re-render either way.
    document.body.appendChild(found);
    sheet = found;
    sheet.addEventListener('click', function (event) {
      if (event.target.closest('[data-bb-glow-sheet-close]')) closeSheet();
    });
    sheet.addEventListener('keydown', onSheetKeydown);
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-bb-glow-sheet-open]');
    if (!toggle) return;
    event.preventDefault();
    if (sheetState === 'open' || sheetState === 'opening') closeSheet();
    else openSheet(toggle);
  });

  // ── Free-shipping progress (country map, currency-safe) ───────────────
  var shipCfg = null;
  var shipReachedThisPage = {};

  function readShipCfg() {
    if (shipCfg !== null) return shipCfg;
    shipCfg = false;
    var el = document.getElementById('bb-ship-cfg');
    if (!el) return shipCfg;
    try {
      shipCfg = JSON.parse(el.textContent);
    } catch (e) {
      shipCfg = false;
    }
    return shipCfg;
  }

  function activeCurrency(cfg) {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) return window.Shopify.currency.active;
    } catch (e) {
      /* fall through */
    }
    return cfg.activeCurrency;
  }

  function shipCountry(cfg) {
    try {
      var saved = JSON.parse(sessionStorage.getItem('bb_ship_addr') || '{}');
      if (saved.country) {
        var code = saved.countryCode || (saved.country.length <= 3 ? saved.country.toUpperCase() : '');
        if (code) return { code: code, name: saved.country.length <= 3 ? code : saved.country };
      }
    } catch (e) {
      /* ignore */
    }
    if (cfg.mode === 'require_address') return null;
    return cfg.shopCountry ? { code: cfg.shopCountry, name: cfg.shopCountryName || cfg.shopCountry } : null;
  }

  function formatShopMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.money_format) {
      return window.Shopify.formatMoney(cents, window.money_format);
    }
    return null;
  }

  function renderShipping(state, previous) {
    var root = pageRoot();
    var slot = root ? root.querySelector('[data-bb-glow-ship]') : null;
    if (!slot) return;
    var hide = function () {
      slot.hidden = true;
    };
    if (!state || root.getAttribute('data-bb-glow-ship') === 'none') return hide();
    var cfg = readShipCfg();
    if (!cfg || cfg.progressShow === false) return hide();

    // Thresholds are in the store currency. A presentment-currency cart is
    // never compared against them (see bb-cart-actions.js for the reasoning).
    var shopCur = String(cfg.thresholdCurrency || '').toUpperCase();
    var cur = String(activeCurrency(cfg) || state.currency || '').toUpperCase();
    if (!shopCur || shopCur !== cur || String(state.currency || cur).toUpperCase() !== shopCur) return hide();

    var country = shipCountry(cfg);
    var threshold = country && cfg.thresholds ? cfg.thresholds[country.code] || 0 : 0;
    if (!threshold) return hide();

    var label = slot.querySelector('[data-bb-glow-ship-label]');
    var pct = Math.max(0, Math.min(1, state.totalCents / threshold));
    slot.style.setProperty('--bbg-ship-pct', String(pct));
    var remaining = threshold - state.totalCents;

    if (remaining > 0) {
      var amount = formatShopMoney(remaining);
      if (!amount) return hide();
      label.innerHTML = 'Add <strong>' + esc(amount) + '</strong> more to reach free shipping to ' + esc(country.name) + '.';
    } else {
      label.innerHTML = '<strong>Free-shipping threshold reached</strong> for ' + esc(country.name) + '.';
      var key = country.code + ':' + threshold;
      var crossed = previous && previous.totalCents < threshold && !shipReachedThisPage[key];
      if (crossed) {
        shipReachedThisPage[key] = true;
        if (rewardsOn(root)) burst(slot);
        announce('Free-shipping threshold reached.');
      }
    }
    slot.hidden = false;
  }

  function burst(host) {
    if (!host || reducedMotion()) return;
    var b = document.createElement('span');
    b.className = 'bbg-burst';
    b.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 8; i++) {
      var dot = document.createElement('i');
      var angle = (Math.PI * 2 * i) / 8;
      dot.style.setProperty('--bx', Math.round(Math.cos(angle) * 28) + 'px');
      dot.style.setProperty('--by', Math.round(Math.sin(angle) * 20) + 'px');
      b.appendChild(dot);
    }
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(b);
    setTimeout(function () {
      b.remove();
    }, 650);
  }

  // ── Reward feedback, once per confirmed state transition ──────────────
  var lastState = { page: null, drawer: null };

  function rowByKey(root, key) {
    if (!root || !key) return null;
    var rows = root.querySelectorAll('.cart-items__table-row[data-bb-line-key]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-bb-line-key') === key) return rows[i];
    }
    return null;
  }

  function diffFeedback(root, prev, next) {
    if (!prev || !next || !rewardsOn(root)) return;

    var prevQty = {};
    (prev.items || []).forEach(function (item) {
      prevQty[item.key] = item.qty;
    });
    (next.items || []).forEach(function (item) {
      if (prevQty[item.key] !== undefined && prevQty[item.key] !== item.qty) {
        flash(rowByKey(root, item.key), 'bbg-fx-updated', 180);
      }
    });

    var before = {};
    (prev.offers || []).forEach(function (title) {
      before[title] = true;
    });
    var fresh = (next.offers || []).filter(function (title) {
      return !before[title];
    });
    if (fresh.length && next.discountCents > prev.discountCents) {
      var strip = root.querySelector('[data-bb-glow-savings]');
      flash(strip, 'bbg-fx-offer', 520);
      burst(strip);
    }
  }

  function announceChange(prev, next) {
    if (!prev || !next) return;
    var before = {};
    (prev.offers || []).forEach(function (title) {
      before[title] = true;
    });
    var fresh = (next.offers || []).filter(function (title) {
      return !before[title];
    });
    if (fresh.length && next.discountCents > prev.discountCents) {
      announce('Offer applied. ' + next.discount + ' saved.');
    } else if (next.count !== prev.count || next.totalCents !== prev.totalCents) {
      announce('Cart updated. ' + next.count + ' ' + plural(next.count) + ', estimated total ' + next.total + '.');
    }
  }

  function refresh() {
    var page = pageRoot();
    var drawer = drawerRoot();

    if (page) {
      var nextPage = readState(page);
      diffFeedback(page, lastState.page, nextPage);
      announceChange(lastState.page, nextPage);
      renderDock(nextPage);
      if (sheetState !== 'closed') renderSheet(nextPage);
      renderShipping(nextPage, lastState.page);
      lastState.page = nextPage;
    }

    if (drawer) {
      var nextDrawer = readState(drawer);
      if (drawer.getAttribute('data-state') !== 'closed') {
        diffFeedback(drawer, lastState.drawer, nextDrawer);
        announceChange(lastState.drawer, nextDrawer);
      }
      lastState.drawer = nextDrawer;
    }
  }

  var refreshTimer = null;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 0);
  }

  // ── Settled cart snapshot ──────────────────────────────────────────────
  var settleSeq = 0;
  var settleTimer = null;

  function busy() {
    return !!(window.BBCart && window.BBCart.busy);
  }

  function stateMatches(state, cart) {
    if (!state || state.count !== cart.item_count || state.totalCents !== cart.total_price) return false;
    var want = {};
    (cart.items || []).forEach(function (item) {
      want[item.key] = item.quantity;
    });
    var items = state.items || [];
    if (items.length !== (cart.items || []).length) return false;
    for (var i = 0; i < items.length; i++) {
      if (want[items[i].key] !== items[i].qty) return false;
    }
    return true;
  }

  // The drawer is stale when its numbers differ from the cart, and also when
  // its shape does: "Your cart is empty" for a cart with items (a page that
  // loaded empty, then came back with Back after an add elsewhere), item rows
  // for an empty cart, or a populated drawer without its More options views.
  function drawerStale(drawer, cart) {
    var showsEmpty = !!drawer.querySelector('.drawer__inner-empty');
    if (!cart.item_count) return !showsEmpty || !!drawer.querySelector('[data-bb-line-key]');
    return showsEmpty || !drawer.querySelector('[data-bb-views]') || !stateMatches(readState(drawer), cart);
  }

  function staleComponents(cart) {
    var out = [];
    var page = pageRoot();
    if (page && page.dataset.sectionId && !stateMatches(readState(page), cart)) out.push(page);
    var drawer = drawerRoot();
    var items = drawer && drawer.querySelector('cart-drawer-items');
    if (items && (items.dataset.sectionId || drawer.dataset.sectionId) && drawerStale(drawer, cart)) out.push(items);
    return out;
  }

  // ── Refreshing / refresh failed (drawer) ───────────────────────────────
  // While a stale drawer is being rebuilt it is marked as updating (dimmed,
  // checkout pending), so an out-of-date cart never reads as settled. If the
  // rebuild fails, a notice says so and offers to try again.
  function setRefreshing(on) {
    var drawer = drawerRoot();
    if (!drawer) return;
    var inner = drawer.querySelector('.drawer__inner');
    if (on) {
      drawer.setAttribute('data-bb-refreshing', 'true');
      if (inner) inner.setAttribute('aria-busy', 'true');
    } else {
      drawer.removeAttribute('data-bb-refreshing');
      if (inner) inner.removeAttribute('aria-busy');
    }
  }

  function clearRefreshError() {
    document.querySelectorAll('[data-bb-refresh-error]').forEach(function (el) {
      el.remove();
    });
  }

  function showRefreshError() {
    var drawer = drawerRoot();
    var inner = drawer && drawer.querySelector('.drawer__inner');
    if (!inner || inner.querySelector('[data-bb-refresh-error]')) return;
    var box = document.createElement('div');
    box.className = 'bb-glow-refresh-error';
    box.setAttribute('role', 'alert');
    box.setAttribute('data-bb-refresh-error', '');
    box.innerHTML =
      '<p class="bb-glow-refresh-error__text">We couldn’t refresh your cart, so it may be out of date.</p>' +
      '<button type="button" class="bb-glow-inline-action" data-bb-refresh-retry>Try again</button>';
    var handle = inner.querySelector('.bb-glow-handle');
    inner.insertBefore(box, handle ? handle.nextSibling : inner.firstChild);
  }

  document.addEventListener('click', function (event) {
    var retry = event.target.closest('[data-bb-refresh-retry]');
    if (!retry) return;
    retry.disabled = true;
    retry.textContent = UPDATING;
    scheduleSettle('retry', 0);
  });

  function rerender(components) {
    var ids = components.map(function (c) {
      return c.dataset.sectionId;
    });
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), {
      cache: 'no-store',
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('sections ' + r.status);
        return r.json();
      })
      .then(function (sections) {
        if (!sections || busy()) return;
        var revision = window.BBCart ? window.BBCart.nextRevision() : 0;
        components.forEach(function (c) {
          var id = c.dataset.sectionId;
          if (typeof c.updateSections !== 'function' || !sections[id]) return;
          var own = {};
          own[id] = sections[id];
          c.updateSections(own, revision);
        });
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { sections: sections, source: 'settled', revision: revision, bbApplied: true } })
        );
      });
  }

  // Reads the cart once the burst is over and makes every surface agree.
  function settle(reason) {
    clearTimeout(settleTimer);
    if (!window.BBCart) {
      refresh();
      setPending(false);
      return;
    }
    var seq = ++settleSeq;
    var revision = window.BBCart.revision;
    // A page restored by Back/Forward may show a cart from before the
    // shopper left: it reads as updating until the cart is confirmed.
    var doubtful = reason === 'pageshow' || reason === 'retry';
    if (doubtful) {
      setRefreshing(true);
      setPending(true);
    }
    window.BBCart.fetchCart()
      .then(function (cart) {
        if (seq !== settleSeq || busy()) return null;
        // Something rendered while the cart was being read: read it again.
        if (window.BBCart.revision !== revision) {
          scheduleSettle(reason, 0);
          return null;
        }
        var stale = staleComponents(cart);
        if (stale.length) {
          setRefreshing(true);
          setPending(true);
        }
        return (stale.length ? rerender(stale) : Promise.resolve()).then(function () {
          if (seq !== settleSeq || busy()) return;
          clearRefreshError();
          setRefreshing(false);
          renderCounts(cart.item_count);
          renderLabels(cart.item_count);
          refresh();
          setPending(false);
          document.dispatchEvent(new CustomEvent('bb:cart:settled', { detail: { cart: cart, reason: reason } }));
        });
      })
      .catch(function () {
        if (seq !== settleSeq || busy()) return;
        var drawer = drawerRoot();
        var wasRefreshing = !!(drawer && drawer.hasAttribute('data-bb-refreshing'));
        setRefreshing(false);
        // Offline: keep checkout usable with what the page last rendered.
        var state = readState(drawer) || readState(pageRoot());
        if (state) renderLabels(state.count);
        refresh();
        setPending(false);
        // The drawer was known (or suspected) to be out of date: say so.
        if (wasRefreshing) showRefreshError();
        document.querySelectorAll('[data-bb-refresh-retry][disabled]').forEach(function (btn) {
          btn.disabled = false;
          btn.textContent = 'Try again';
        });
      });
  }

  function scheduleSettle(reason, delay) {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      if (!busy()) settle(reason);
    }, delay || 0);
  }

  // Section HTML is swapped synchronously right after these events fire.
  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('DiscountUpdateEvent', function () {
    scheduleRefresh();
    scheduleSettle('discount', 50);
  });
  document.addEventListener('GiftWrapUpdateEvent', scheduleRefresh);

  document.addEventListener('cart:mutation-start', function () {
    settleSeq++;
    setPending(true);
  });
  document.addEventListener('cart:mutation-end', function () {
    scheduleSettle('mutation', 0);
  });
  document.addEventListener('cart-drawer:open', function () {
    if (!busy()) scheduleSettle('drawer-open', 30);
  });

  // Express buttons can't be pressed while a cart change is in flight, so
  // neither checkout path starts from a cart that is about to change.
  function lockExpress(on) {
    document.querySelectorAll('[data-bb-express-host], .cart__dynamic-checkout-buttons').forEach(function (host) {
      host.inert = !!on;
      host.toggleAttribute('data-bb-express-pending', !!on);
    });
  }

  // Dock "Express checkout options": to the summary's wallet buttons.
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-bb-dock-express]')) return;
    var host = document.querySelector('cart-items-component .cart__dynamic-checkout-buttons');
    if (!host) return;
    var box = host.closest('[data-bb-express]');
    if (box && !expressOpen) {
      expressOpen = true;
      applyExpress();
    }
    host.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    var focusable = (box && box.querySelector('[data-bb-express-toggle]')) || host;
    if (!focusable.hasAttribute('tabindex') && focusable === host) focusable.setAttribute('tabindex', '-1');
    focusable.focus({ preventScroll: true });
  });

  // ── Express checkout disclosure (mobile, optional) ───────────────────
  // Opening it shows Shopify's own express buttons; the choice survives the
  // summary re-rendering after a cart change.
  var expressOpen = false;

  function applyExpress() {
    document.querySelectorAll('[data-bb-express]').forEach(function (box) {
      var toggle = box.querySelector('[data-bb-express-toggle]');
      var panel = box.querySelector('[data-bb-express-panel]');
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', expressOpen ? 'true' : 'false');
      box.toggleAttribute('data-open', expressOpen);
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-bb-express-toggle]');
    if (!toggle) return;
    expressOpen = toggle.getAttribute('aria-expanded') !== 'true';
    applyExpress();
  });
  document.addEventListener('cart:updated', function () {
    setTimeout(applyExpress, 0);
  });

  var hiddenAt = 0;
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (hiddenAt && Date.now() - hiddenAt > 2000 && !busy()) scheduleSettle('visible', 0);
    hiddenAt = 0;
  });

  // Confirmed add-to-cart (from the drawer's renderContents): the new or
  // updated line gets one rim highlight once the drawer content is in.
  document.addEventListener('cart:item-added', function (event) {
    var keys = [];
    if (event.detail && event.detail.key) keys.push(event.detail.key);
    if (event.detail && event.detail.keys) keys = keys.concat(event.detail.keys);
    var drawer = drawerRoot();
    setTimeout(function () {
      var state = readState(drawer);
      if (drawer && rewardsOn(drawer)) {
        keys.forEach(function (key) {
          flash(rowByKey(drawer, key), 'bbg-fx-added', 240);
        });
      }
      if (state) announce('Added to cart. ' + state.count + ' ' + plural(state.count) + ' in your cart.');
      lastState.drawer = state;
    }, 0);
  });

  // ── Purchase points estimate (data-ready) ──────────────────────────────
  // Nothing in the theme invents a points figure. A verified estimate (from
  // the rewards service, once it provides one) arrives as:
  //   bb:points:estimate { revision, basis: 'merchandise', currency, total,
  //                        lines: { lineKey: n } }
  // It covers the items only: points for shipping and tax are settled when
  // the paid order is awarded, so the total is labelled "from items".
  // revision must be the BBCart revision it was quoted for; a quote for an
  // older cart is ignored. Without an estimate the slots stay hidden, and a
  // failed estimate hides them rather than showing 0.
  var pointsShown = { revision: null, total: null };

  function pointsText(n) {
    return 'Est. +' + n + ' ' + (n === 1 ? 'point' : 'points');
  }

  function hidePoints() {
    document.querySelectorAll('[data-bb-points-line], [data-bb-points-total]').forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
    watchGleam();
  }

  // ── Points gloss: a diagonal sweep across estimates, every few seconds ──
  // Only estimates on screen sweep, only while their cart is visible (the
  // drawer open, or the cart page) and the tab is active; each one starts a
  // little after the last, so they never flash together. Nothing is
  // announced, and reduced motion keeps them still. Settings: Theme
  // settings > Liquid Glow > Points.
  var gleam = { timer: null, onScreen: [], io: null };

  function gleamSetting(name, fallback) {
    var host = document.querySelector('.bb-glow') || document.documentElement;
    var n = parseFloat(getComputedStyle(host).getPropertyValue(name));
    return isNaN(n) ? fallback : n;
  }

  function gleamTargets() {
    return gleam.onScreen.filter(function (el) {
      if (!el.isConnected || el.hidden) return false;
      var drawer = el.closest('cart-drawer');
      return !drawer || drawer.getAttribute('data-state') === 'open';
    });
  }

  function sweepPoints() {
    gleam.timer = null;
    if (document.hidden || reducedMotion()) return;
    gleamTargets().forEach(function (el, i) {
      setTimeout(function () {
        el.classList.remove('is-gleaming');
        void el.offsetWidth;
        el.classList.add('is-gleaming');
      }, i * 220 + Math.round(Math.random() * 120));
    });
    scheduleGleam();
  }

  function scheduleGleam() {
    clearTimeout(gleam.timer);
    gleam.timer = null;
    if (!gleam.onScreen.length || document.hidden || reducedMotion() || gleamSetting('--bbg-gleam-on', 1) === 0) return;
    var every = Math.min(12, Math.max(3, gleamSetting('--bbg-gleam-every', 5)));
    gleam.timer = setTimeout(sweepPoints, every * 1000);
  }

  // Re-read which estimates are showing (slots are replaced on re-render).
  function watchGleam() {
    if (gleam.io) gleam.io.disconnect();
    gleam.onScreen = [];
    var shown = Array.from(document.querySelectorAll('[data-bb-points-line]:not([hidden]), [data-bb-points-total]:not([hidden])'));
    if (!shown.length || typeof IntersectionObserver === 'undefined') return scheduleGleam();
    gleam.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var at = gleam.onScreen.indexOf(entry.target);
        if (entry.isIntersecting && at === -1) gleam.onScreen.push(entry.target);
        if (!entry.isIntersecting && at !== -1) gleam.onScreen.splice(at, 1);
      });
      if (!gleam.timer) scheduleGleam();
    });
    shown.forEach(function (el) {
      gleam.io.observe(el);
    });
  }

  document.addEventListener('animationend', function (event) {
    if (event.animationName === 'bbg-gleam') event.target.classList.remove('is-gleaming');
  });
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      clearTimeout(gleam.timer);
      gleam.timer = null;
    } else {
      scheduleGleam();
    }
  });

  function renderPoints(est) {
    if (!est || typeof est.total !== 'number' || est.total < 0) return hidePoints();
    if (est.basis && est.basis !== 'merchandise') return hidePoints();
    if (window.BBCart && est.revision !== undefined && est.revision !== window.BBCart.revision) return;
    var lines = est.lines || {};
    document.querySelectorAll('[data-bb-points-line]').forEach(function (el) {
      var row = el.closest('[data-bb-line-key]');
      var n = row ? lines[row.getAttribute('data-bb-line-key')] : undefined;
      el.hidden = typeof n !== 'number' || n <= 0;
      el.textContent = el.hidden ? '' : pointsText(n);
    });
    var increased = pointsShown.total !== null && est.total > pointsShown.total && pointsShown.revision !== est.revision;
    document.querySelectorAll('[data-bb-points-total]').forEach(function (el) {
      el.hidden = est.total <= 0;
      el.textContent = el.hidden ? '' : 'Estimated points from items: ' + est.total;
      if (increased && !el.hidden) {
        var root = el.closest('.bb-glow');
        if (rewardsOn(root)) {
          flash(el, 'bbg-fx-points', 480);
          burst(el);
        }
      }
    });
    if (est.total > 0 && (pointsShown.total !== est.total || pointsShown.revision !== est.revision)) {
      announce('Estimated points from items: ' + est.total + '.');
    }
    pointsShown = { revision: est.revision === undefined ? null : est.revision, total: est.total };
    watchGleam();
  }

  document.addEventListener('bb:points:estimate', function (event) {
    renderPoints(event.detail);
  });
  document.addEventListener('bb:points:unavailable', hidePoints);

  // ── Unfinished option change (after a reload or a reopened drawer) ─────
  // BBCart remembers a swap whose new option landed but whose original line
  // could not be reduced. While the cart is still exactly in that state, a
  // notice offers to finish it or keep both. Nothing happens on its own.
  var recoveryBusy = false;

  function removeRecoveryNotices() {
    document.querySelectorAll('[data-bb-recovery]').forEach(function (el) {
      el.remove();
    });
  }

  function recoveryHosts() {
    var hosts = [];
    var page = pageRoot();
    var slot = page && page.querySelector('[data-bb-restore-slot]');
    if (slot) hosts.push({ el: slot, mode: 'append' });
    var drawer = drawerRoot();
    var items = drawer && drawer.querySelector('cart-drawer-items');
    if (items) hosts.push({ el: items, mode: 'before' });
    return hosts;
  }

  function recoveryNotice(pending) {
    var to = (pending.moved || [])
      .map(function (m) {
        return m.title;
      })
      .filter(Boolean)
      .join(', ');
    var box = document.createElement('div');
    box.className = 'bb-glow-recovery';
    box.setAttribute('role', 'status');
    box.setAttribute('data-bb-recovery', pending.id);
    box.innerHTML =
      '<p class="bb-glow-recovery__text"><strong>Your last option change didn\u2019t finish.</strong> ' +
      esc(pending.product || 'An item') +
      ': ' +
      esc(to || 'the new option') +
      ' was added, but ' +
      esc(pending.from || 'the original option') +
      ' is still in your cart too.</p>' +
      '<div class="bb-glow-recovery__actions">' +
      '<button type="button" class="bb-glow-inline-action" data-bb-recovery-fix>Fix my cart</button>' +
      '<button type="button" class="bb-glow-inline-action bb-glow-inline-action--quiet" data-bb-recovery-keep>Keep both</button>' +
      '</div>';
    return box;
  }

  function showRecovery(pending) {
    recoveryHosts().forEach(function (host) {
      var existing = host.mode === 'before' ? host.el.previousElementSibling : host.el.querySelector('[data-bb-recovery]');
      if (existing && existing.getAttribute && existing.getAttribute('data-bb-recovery') === pending.id) return;
      var box = recoveryNotice(pending);
      if (host.mode === 'before') host.el.parentNode.insertBefore(box, host.el);
      else host.el.appendChild(box);
    });
  }

  function checkRecovery() {
    if (!window.BBCart || !window.BBCart.loadPendingEdit || recoveryBusy) return;
    var pending = window.BBCart.loadPendingEdit();
    if (!pending) return removeRecoveryNotices();
    if (busy()) return;
    window.BBCart.fetchCart()
      .then(function (cart) {
        var state = window.BBCart.inspectEdit(pending, cart);
        if (state !== 'pending') {
          window.BBCart.clearPendingEdit(pending);
          removeRecoveryNotices();
          return;
        }
        showRecovery(pending);
      })
      .catch(function () {
        /* offline: try again on the next settle */
      });
  }

  document.addEventListener('click', function (event) {
    var fix = event.target.closest('[data-bb-recovery-fix]');
    var keep = event.target.closest('[data-bb-recovery-keep]');
    if (!fix && !keep) return;
    var box = event.target.closest('[data-bb-recovery]');
    var pending = window.BBCart && window.BBCart.loadPendingEdit();
    if (!pending || !box || box.getAttribute('data-bb-recovery') !== pending.id) {
      removeRecoveryNotices();
      return;
    }
    if (keep) {
      window.BBCart.clearPendingEdit(pending);
      removeRecoveryNotices();
      announce('Both options stay in your cart.');
      return;
    }
    if (recoveryBusy) return;
    recoveryBusy = true;
    document.querySelectorAll('[data-bb-recovery-fix]').forEach(function (b) {
      b.disabled = true;
      b.textContent = UPDATING;
    });
    window.BBCart.run(function () {
      return window.BBCart.finishEdit(pending);
    })
      .then(function (result) {
        recoveryBusy = false;
        if (result.status === 'failed') {
          document.querySelectorAll('[data-bb-recovery-fix]').forEach(function (b) {
            b.disabled = false;
            b.textContent = 'Fix my cart';
          });
          announce('Your cart couldn\u2019t be updated. Try again.');
          return;
        }
        removeRecoveryNotices();
        announce(result.status === 'done' ? 'Cart fixed.' : 'Your cart changed since then, so nothing was removed.');
      })
      .catch(function () {
        recoveryBusy = false;
        document.querySelectorAll('[data-bb-recovery-fix]').forEach(function (b) {
          b.disabled = false;
          b.textContent = 'Fix my cart';
        });
      });
  });

  document.addEventListener('bb:cart:settled', checkRecovery);
  document.addEventListener('cart-drawer:open', function () {
    setTimeout(checkRecovery, 60);
  });

  // Save for later: the heart fills when the wishlist confirms the save.
  window.addEventListener('bb:wl:popup-saved', function (event) {
    var id = event.detail && event.detail.productId;
    if (!id) return;
    document.querySelectorAll('.bb-glow .bb-sfl-btn[data-sfl-product-id="' + String(id).replace(/"/g, '') + '"]').forEach(
      function (btn) {
        btn.classList.add('is-saved');
        btn.setAttribute('aria-label', 'Saved for later');
      }
    );
  });

  // ── Boot ───────────────────────────────────────────────────────────────
  function init() {
    rememberCheckoutWord();
    initSheet();
    measureDock();
    lastState.page = readState(pageRoot());
    lastState.drawer = readState(drawerRoot());
    renderDock(lastState.page);
    renderShipping(lastState.page, null);
    checkRecovery();
    // Back/Forward can also reload the page from the browser's HTTP cache:
    // HTML from before the shopper left, without a persisted pageshow.
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type === 'back_forward') scheduleSettle('pageshow', 0);
    } catch (e) {
      /* no navigation timing: the drawer still re-reads the cart when it opens */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-bind after the section reloads.
  document.addEventListener('shopify:section:load', function () {
    shipCfg = null;
    setTimeout(init, 0);
  });
})();
